import { Button, Spinner } from 'react-bootstrap';
import type { ButtonProps } from 'react-bootstrap';
import type { ReactNode } from 'react';

export interface BusyButtonProps extends ButtonProps {
  /** When true, the button shows a spinner, is disabled, and (if provided) shows `busyLabel`. */
  loading?: boolean;
  /** Optional label to show while loading (e.g. "Opening…"). Falls back to the normal children. */
  busyLabel?: ReactNode;
}

/**
 * A Button that gives immediate feedback for actions that hit the network (a click that fires an
 * API call and then acts on the result). While `loading`, it shows an inline spinner, disables
 * itself (prevents double-submit), and optionally swaps in a `busyLabel` so the user can see the
 * click registered — instead of a control that looks frozen for the ~second a Lambda round-trip
 * takes. Drop-in replacement for react-bootstrap's Button; all other props pass through.
 */
export function BusyButton({
  loading = false,
  busyLabel,
  disabled,
  children,
  ...rest
}: BusyButtonProps) {
  return (
    <Button {...rest} disabled={disabled || loading}>
      {loading && <Spinner as="span" size="sm" animation="border" className="me-2" />}
      {loading ? (busyLabel ?? children) : children}
    </Button>
  );
}
